/**
 * MONSTER TOWN — SHARED TYPES. FROZEN CONTRACT (src/core/*). Additive only.
 *
 * Anything that crosses a crew boundary lives here. If two crews need to agree
 * on a shape, it belongs in this file. Adding a new field to an existing
 * interface is allowed ONLY if it is optional (`?`) — six crews compile against
 * these and a required field breaks all of them at once.
 */

import type { Cell, Rot } from './grid'
import type { PaletteName } from './palette'

// ---------------------------------------------------------------------------
// primitives
// ---------------------------------------------------------------------------

export interface Vec3 {
  x: number
  y: number
  z: number
}
export interface Vec2 {
  x: number
  y: number
}

/** A tuple that cannot be empty. Used to make "no feedback" a compile error. */
export type NonEmpty<T> = readonly [T, ...T[]]

/** Branded id helpers so ids never get crossed. */
export type PlaceableId = string & { readonly __brand?: 'PlaceableId' }
export type InstanceId = string & { readonly __brand?: 'InstanceId' }
export type SceneId = string & { readonly __brand?: 'SceneId' }
export type NpcId = string & { readonly __brand?: 'NpcId' }
export type VenueId = string & { readonly __brand?: 'VenueId' }

export type SaveSlot = 'a' | 'b'

/** The one toggle. Build = place things, Play = live in them. */
export type GameMode = 'build' | 'play'

/**
 * Coarse app phase. Used by the harness and the HUD crew to know what UI to
 * show. Never a difficulty setting — LAW #3, there are no age modes.
 */
export type Phase =
  | 'boot'
  | 'slotPick'
  | 'monsterCreator'
  | 'town'
  | 'houseInterior'
  | 'venueInterior'
  | 'shop'
  | 'photo'

// ---------------------------------------------------------------------------
// the player's monster
// ---------------------------------------------------------------------------

export type MonsterBodyShape = 'blob' | 'tall' | 'round' | 'chunky'
export type MonsterEyes = 'two' | 'one' | 'three' | 'sleepy'
export type MonsterHorns = 'none' | 'nubs' | 'curved' | 'antlers' | 'antennae'
export type MonsterPattern = 'plain' | 'spots' | 'stripes' | 'belly' | 'star'

export interface MonsterConfig {
  shape: MonsterBodyShape
  /** Palette key — see MONSTER_BODY_COLORS. */
  color: PaletteName
  accentColor: PaletteName
  eyes: MonsterEyes
  horns: MonsterHorns
  pattern: MonsterPattern
  /** Earned later; empty until unlocked. */
  hat: string | null
  outfit: string | null
}

export const DEFAULT_MONSTER: MonsterConfig = {
  shape: 'blob',
  color: 'monsterTeal',
  accentColor: 'monsterBelly',
  eyes: 'two',
  horns: 'nubs',
  pattern: 'belly',
  hat: null,
  outfit: null,
}

// ---------------------------------------------------------------------------
// placed things
// ---------------------------------------------------------------------------

/** One placed object. This is the atom of everything the kid builds. */
export interface PlacedInstance {
  id: InstanceId
  defId: PlaceableId
  sceneId: SceneId
  anchor: Cell
  rot: Rot
  /** Per-instance mutable state owned by the placeable's interaction handler. */
  state?: Record<string, unknown>
  /** Optional recolour chosen by the kid (palette key). */
  tint?: PaletteName
}

// ---------------------------------------------------------------------------
// town / world state
// ---------------------------------------------------------------------------

export interface TownState {
  name: string
  /** Real-world ms timestamp of last session end — drives "while you were away". */
  lastSeenAt: number
  /** Total in-world days elapsed. Cosmetic; there is no clock pressure. */
  day: number
  /** Ids of scenes the kid has unlocked (house rooms, venue plots). */
  unlockedScenes: SceneId[]
}

// ---------------------------------------------------------------------------
// venues
// ---------------------------------------------------------------------------

export type VenueKind = 'restaurant' | 'iceCream' | 'zoo' | 'garden' | 'park' | 'petShop' | 'school'

/** One thing a customer asked for. Pictures, never words. LAW #4. */
export interface OrderItem {
  /** Content id of the food/product, e.g. 'scoop_strawberry'. */
  itemId: string
  count: number
  /** Optional topping/extra content id. */
  extraId?: string
}

export interface CustomerOrder {
  id: string
  npcId: NpcId
  items: OrderItem[]
  /** What the player has assembled so far. Never times out. LAW #2. */
  assembled: OrderItem[]
  /** 'waiting' customers wait happily forever. */
  status: 'walkingIn' | 'seated' | 'ordering' | 'waiting' | 'served' | 'leaving'
  seatInstanceId?: InstanceId
}

export interface VenueState {
  id: VenueId
  kind: VenueKind
  sceneId: SceneId
  open: boolean
  /** Lifetime counters — used for gentle unlocks, never for scoring or failure. */
  servedCount: number
  tipsEarned: number
  activeOrders: CustomerOrder[]
}

// ---------------------------------------------------------------------------
// NPC life
// ---------------------------------------------------------------------------

export type NpcMood = 'happy' | 'delighted' | 'curious' | 'sleepy' | 'hungry'

export interface NpcState {
  id: NpcId
  /** Neighbours are monsters too — same creator config space. */
  look: MonsterConfig
  name: string
  sceneId: SceneId
  /** World position; NPCs are not grid-bound, they walk freely on walkable tiles. */
  pos: Vec3
  mood: NpcMood
  /** Current behaviour node id, owned by the NPC crew. */
  activity: string
  /** True once the kid has met them; drives "a new monster arrived". */
  known: boolean
}

// ---------------------------------------------------------------------------
// wallet / progression
// ---------------------------------------------------------------------------

export interface WalletState {
  coins: number
  /** Lifetime earned — drives the unlock ladder, never spent. */
  lifetimeEarned: number
  /** Unlocked tier ids. */
  tiers: string[]
  /** Placeable ids bought/unlocked beyond the free-forever set. */
  owned: PlaceableId[]
}

// ---------------------------------------------------------------------------
// misc
// ---------------------------------------------------------------------------

/** Named world locations the harness can teleport to. */
export interface NamedSpot {
  name: string
  sceneId: SceneId
  pos: Vec3
  /** Camera yaw quarter-turn to use on arrival. */
  rot?: Rot
}

export interface FrameStats {
  fps: number
  /** Wall-clock interval between frames. Clamped by the display — informational. */
  frameMs: number
  /** CPU ms spent inside our sim + render systems last frame. THE real signal. */
  workMs: number
  drawCalls: number
  triangles: number
  programs: number
  renderScale: number
  pixelRatio: number
}
